import React from 'react'
import Downshift, {
  GetToggleButtonPropsOptions,
  GetItemPropsOptions
} from 'downshift'
import { css, cx } from '@emotion/css'
import { ThemeColors } from '@contrast-security-inc/design-system-foundations'
import { Selection } from './selection'
import { Menu, MenuItemRenderer } from './menu'
import { LoadingMaskRectangle } from '../loading-masks'

export interface DropdownStyleOverrides {
  readonly outerContainerCss?: string
  readonly selectionContainerCss?: string
  readonly selectionOpenContainerCss?: string
  readonly selectionTextCss?: string
  readonly menuCss?: string
  readonly menuItemContainerCss?: string
  readonly menuItemActiveContainerCss?: string
  readonly menuItemTextCss?: string
  readonly menuItemSelectedTextCss?: string
  readonly arrowColor?: ThemeColors
}

type ItemToString<T> = (item: T) => string
type GetButtonProps = (options?: GetToggleButtonPropsOptions) => any

export type SelectionToggleRenderer<T> = (
  props: SelectionToggleRendererProps<T>
) => React.ReactElement<any>

interface SelectionToggleRendererProps<T> {
  readonly isOpen: boolean
  readonly selectedItem?: T
  readonly getToggleButtonProps: GetButtonProps
  readonly selectionItemRenderer?: SelectionItemRenderer<T>
  readonly itemSelectionToString?: ItemToString<T>
  readonly itemToString?: ItemToString<T>
  readonly styleOverrides?: DropdownStyleOverrides
  readonly hideArrow?: boolean
  readonly e2eTestId?: string
  readonly unitTestId?: string
  readonly ariaLabel?: string
}

export type MenuRenderer<T> = (
  props: MenuRendererProps<T>
) => React.ReactElement<any>

interface MenuRendererProps<T> {
  readonly items: T[]
  readonly selectedItem?: T
  readonly itemToString: ItemToString<T>
  readonly itemToKey?: ItemToString<T>
  readonly itemToGroup?: ItemToString<T>
  readonly isItemDisabled?: (item: T) => boolean
  readonly highlightedIndex: number | undefined
  readonly getItemProps: (options: GetItemPropsOptions<T>) => any
  readonly menuItemRenderer?: MenuItemRenderer<T>
  readonly menuFooterRenderer?: () => React.ReactElement<any>
  readonly styleOverrides?: DropdownStyleOverrides
  readonly e2eTestId?: string
}

export type SelectionItemRenderer<T> = (
  props: SelectionRenderProps<T>
) => React.ReactElement<any>

interface SelectionRenderProps<T> {
  readonly item: T
}

export interface DropdownProps<T> {
  /**
   * An array of data.
   */
  readonly items: T[]

  /**
   * The selection. This component is stateless, so the selection
   * state must be handled higher up in the component heirarchy.
   */
  readonly selectedItem?: T

  /**
   * A function to determine what will be displayed for each item.
   */
  readonly itemToString: ItemToString<T>

  /**
   * An optional function to determine what will be displayed for the selected item.
   */
  readonly itemSelectionToString?: ItemToString<T>

  /**
   * A function to determine a unique key for each item. Defaults to
   * the value of itemToString.
   */
  readonly itemToKey?: ItemToString<T>

  /**
   * A function to group items.
   */
  readonly itemToGroup?: ItemToString<T>

  /**
   * An optional function to determine if an item should be disabled
   */
  readonly isItemDisabled?: (item: T) => boolean

  /**
   * A callback that will be invoked when a new item is selected.
   */
  readonly onChange?: (item: T) => void

  /**
   * A callback that will be invoked when an item is selected regardless of
   * whether or not it is already selected.
   */
  readonly onSelect?: (item: T) => void

  /**
   * An optional boolean for controlling the open state of the dropdown.
   */
  readonly isOpen?: boolean

  /**
   * An optional function that gets called when the user clicks outside of the dropdown.
   */
  readonly onOuterClick?: () => void

  /**
   * A configuration of classNames to be applied to various elements
   * in order to allow customization of dropdown styling.
   */
  readonly styleOverrides?: DropdownStyleOverrides

  /**
   * An optional renderer function for the selected item toggle (the container around
   * selectionItemRenderer). Will call a default selectionItemRenderer, too.
   */
  readonly selectionToggleRenderer?: SelectionToggleRenderer<T>

  /**
   * An optional render function for the selected item. Defaults to rendering the result
   * of itemSelectionToString, if provided, itemToString otherwise.
   */
  readonly selectionItemRenderer?: SelectionItemRenderer<T>

  /**
   * An optional render function for each item in the menu. Defaults to rendering the result
   * of itemToString.
   */
  readonly menuItemRenderer?: MenuItemRenderer<T>

  /**
   * An optional render function to render something at the bottom of the dropdown menu
   */
  readonly menuFooterRenderer?: () => React.ReactElement<any>

  /**
   * An optional renderer function for the dropdown menu
   */
  readonly menuRenderer?: MenuRenderer<T>

  readonly hideArrow?: boolean

  readonly e2eTestId?: string

  readonly menuE2eTestId?: string

  readonly unitTestId?: string
}

const defaultOuterContainerCss = css`
  flex: 0 0 auto;
  width: 150px;
  line-height: initial;
  min-width: 70px;
`

const innerContainerCss = css`
  position: relative;
`

export type DropdownFn<T> = (props: DropdownProps<T>) => React.ReactElement<any>

function getDefaultSelectionToggleRendererChildren<T>({
  selectionItemRenderer,
  selectedItem,
  itemSelectionToString,
  itemToString
}: Partial<SelectionToggleRendererProps<T>>): React.ReactNode {
  let children: React.ReactNode

  if (selectionItemRenderer && selectedItem) {
    children = selectionItemRenderer({ item: selectedItem })
  } else if (selectedItem) {
    if (itemSelectionToString) {
      children = itemSelectionToString(selectedItem)
    } else {
      // eslint-disable-next-line unicorn/prefer-ternary
      if (itemToString) {
        children = itemToString(selectedItem)
      } else {
        children = ''
      }
    }
  } else {
    children = ''
  }

  return children
}

export function defaultSelectionToggleRenderer<T>({
  isOpen,
  styleOverrides,
  getToggleButtonProps,
  hideArrow,
  e2eTestId,
  unitTestId,
  ariaLabel,
  ...getChildrenProps
}: SelectionToggleRendererProps<T>) {
  return (
    <Selection
      e2eTestId={e2eTestId}
      data-testid={unitTestId}
      isOpen={isOpen}
      children={getDefaultSelectionToggleRendererChildren(getChildrenProps)}
      containerCss={styleOverrides && styleOverrides.selectionContainerCss}
      openContainerCss={
        styleOverrides && styleOverrides.selectionOpenContainerCss
      }
      textCss={styleOverrides && styleOverrides.selectionTextCss}
      arrowColor={styleOverrides && styleOverrides.arrowColor}
      hideArrow={hideArrow}
      {...getToggleButtonProps(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    />
  )
}

export function defaultMenuRenderer<T>({
  items,
  selectedItem,
  itemToString,
  itemToKey,
  itemToGroup,
  isItemDisabled,
  highlightedIndex,
  getItemProps,
  menuItemRenderer,
  menuFooterRenderer,
  styleOverrides,
  e2eTestId
}: MenuRendererProps<T>) {
  return (
    <Menu
      e2eTestId={e2eTestId}
      items={items}
      selectedItem={selectedItem}
      itemToString={itemToString}
      itemToKey={itemToKey || itemToString}
      itemToGroup={itemToGroup}
      isItemDisabled={isItemDisabled}
      highlightedIndex={highlightedIndex || 0}
      getItemProps={getItemProps}
      menuItemRenderer={menuItemRenderer}
      menuCss={styleOverrides && styleOverrides.menuCss}
      menuItemContainerCss={
        styleOverrides && styleOverrides.menuItemContainerCss
      }
      menuItemActiveContainerCss={
        styleOverrides && styleOverrides.menuItemActiveContainerCss
      }
      menuItemTextCss={styleOverrides && styleOverrides.menuItemTextCss}
      menuItemSelectedTextCss={
        styleOverrides && styleOverrides.menuItemSelectedTextCss
      }
      menuFooterRenderer={menuFooterRenderer}
    />
  )
}

const Layout = ({
  styleOverrides,
  children
}: {
  readonly styleOverrides?: DropdownStyleOverrides
  readonly children: React.ReactNode
}) => {
  return (
    <div
      className={cx(
        'legacy-ds-dropdown',
        defaultOuterContainerCss,
        styleOverrides && styleOverrides.outerContainerCss
      )}
    >
      <div className={innerContainerCss}>{children}</div>
    </div>
  )
}

export function DropdownLoadingView({
  styleOverrides
}: {
  readonly styleOverrides?: DropdownStyleOverrides
}) {
  return (
    <Layout styleOverrides={styleOverrides}>
      <LoadingMaskRectangle width={150} height={35} />
    </Layout>
  )
}

export function Dropdown<T>({
  items,
  selectedItem,
  onChange,
  onSelect,
  isOpen,
  onOuterClick,
  itemToString,
  itemSelectionToString,
  itemToKey,
  itemToGroup,
  isItemDisabled,
  styleOverrides,
  selectionItemRenderer,
  menuItemRenderer,
  menuFooterRenderer,
  selectionToggleRenderer = defaultSelectionToggleRenderer,
  menuRenderer = defaultMenuRenderer,
  hideArrow,
  e2eTestId,
  menuE2eTestId,
  unitTestId,
  ...props
}: DropdownProps<T>) {
  return (
    <Layout styleOverrides={styleOverrides}>
      <Downshift
        selectedItem={selectedItem}
        onChange={onChange}
        onSelect={onSelect}
        itemToString={itemToString}
        isOpen={isOpen}
        onOuterClick={onOuterClick}
      >
        {({ isOpen, getToggleButtonProps, getItemProps, highlightedIndex }) => {
          return (
            <div className={innerContainerCss}>
              {selectionToggleRenderer({
                isOpen,
                selectionItemRenderer,
                itemSelectionToString,
                itemToString,
                styleOverrides,
                getToggleButtonProps,
                selectedItem,
                hideArrow,
                e2eTestId,
                unitTestId,
                ariaLabel: props['aria-label']
              })}
              {isOpen &&
                menuRenderer({
                  items,
                  selectedItem,
                  itemToString,
                  itemToKey,
                  itemToGroup,
                  isItemDisabled,
                  highlightedIndex: highlightedIndex || undefined,
                  getItemProps,
                  menuItemRenderer,
                  styleOverrides,
                  menuFooterRenderer,
                  e2eTestId: menuE2eTestId
                })}
            </div>
          )
        }}
      </Downshift>
    </Layout>
  )
}
